import { useCallback, useEffect, useState, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import {
  apiGetBandAvailabilityLoad,
  apiWhoIsFree,
  type AvailabilityLoadMember,
  type WhoIsFreeMember,
  type WhoIsFreeCategory,
} from '../api';
import PageHeader from '../components/PageHeader';
import PageLoadError from '../components/PageLoadError';

/** Today as yyyy-mm-dd (local date). */
function todayISO(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** '2026-09-07' → 'Sep 7' style short label (browser locale). */
function fmtShort(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Crew Planning V2 — read-only vision tool (manager/team). Two views:
 *   1. LOAD: who is unavailable when over the next ~60 days, crossed with the
 *      band's shows (per active member).
 *   2. WHO IS FREE: pick a date (optionally a category) → members with no
 *      declared unavailability that day, flagging those already on a show.
 * All data is read from the backend; writing stays in Account/Team (V1).
 */
export default function AvailabilityPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const { activeProfileId, getProfile } = useProfileContext();
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;

  // --- Load view state ---
  const [loadMembers, setLoadMembers] = useState<AvailabilityLoadMember[]>([]);
  const [loadLoading, setLoadLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // --- Who-is-free state ---
  const [wDate, setWDate] = useState(todayISO());
  const [wCategory, setWCategory] = useState<WhoIsFreeCategory>('all');
  const [freeMembers, setFreeMembers] = useState<WhoIsFreeMember[]>([]);
  const [freeLoading, setFreeLoading] = useState(false);
  const [freeError, setFreeError] = useState<string | null>(null);

  const reloadLoad = useCallback(() => {
    if (!activeProfileId) return;
    setLoadLoading(true);
    setLoadError(null);
    apiGetBandAvailabilityLoad(activeProfileId)
      .then((rows) => setLoadMembers(rows || []))
      .catch((err) => setLoadError(err?.message || 'Failed to load availability'))
      .finally(() => setLoadLoading(false));
  }, [activeProfileId]);

  useEffect(() => { reloadLoad(); }, [reloadLoad]);

  const runWhoIsFree = useCallback(() => {
    if (!activeProfileId) return;
    if (!wDate) return;
    setFreeLoading(true);
    setFreeError(null);
    apiWhoIsFree(activeProfileId, { date: wDate, category: wCategory })
      .then((rows) => setFreeMembers(rows || []))
      .catch((err) => setFreeError(err?.message || 'Failed to load'))
      .finally(() => setFreeLoading(false));
  }, [activeProfileId, wDate, wCategory]);

  // Auto-query who-is-free on first load (today, all categories).
  useEffect(() => {
    if (activeProfileId) runWhoIsFree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId]);

  const bandName = activeProfile?.name;
  const pageTitle = bandName
    ? t('availabilityV2.pageTitleBand', { band: bandName })
    : t('availabilityV2.pageTitle');

  // Lot 3a: the LOAD view has its own inline message already, but this page
  // shows TWO reads (the next-60-days load AND who-is-free): with both down the
  // page is a void of empty cards. The shared state takes over, and its Retry
  // re-runs BOTH reads (never a bare page reload).
  if (activeProfileId && loadError && freeError) {
    return (
      <PageLoadError
        title={pageTitle}
        detail={loadError}
        onRetry={() => {
          reloadLoad();
          runWhoIsFree();
        }}
      />
    );
  }

  return (
    <div ref={denseRef} className="page" data-density="socle">
      <PageHeader title={pageTitle} />
      <p style={{ color: 'var(--text-dim)', marginTop: -8, marginBottom: 22, maxWidth: 760 }}>
        {t('availabilityV2.pageHint')}
      </p>

      {!activeProfileId ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: 'var(--text-dim)' }}>{t('availabilityV2.selectBand')}</p>
        </div>
      ) : (
        <>
          {/* ---------- Who is free ---------- */}
          <section className="card" style={{ padding: 18, marginBottom: 22 }}>
            <h2 style={{ marginTop: 0, marginBottom: 6, fontSize: '1.1rem' }}>
              🆓 {t('availabilityV2.whoIsFree')}
            </h2>
            <p style={{ color: 'var(--text-dim)', margin: '0 0 14px', fontSize: '0.9rem' }}>
              {t('availabilityV2.whoIsFreeHint')}
            </p>
            <div className="filters" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="av-wif-date">{t('availabilityV2.date')}</label>
                <input
                  id="av-wif-date"
                  type="date"
                  className="form-input"
                  value={wDate}
                  onChange={(e) => setWDate(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label htmlFor="av-wif-cat">{t('availabilityV2.category')}</label>
                <select
                  id="av-wif-cat"
                  className="form-input"
                  value={wCategory}
                  onChange={(e) => setWCategory(e.target.value as WhoIsFreeCategory)}
                >
                  <option value="all">{t('availabilityV2.all')}</option>
                  <option value="musician">{t('availabilityV2.musician')}</option>
                  <option value="crew">{t('availabilityV2.crew')}</option>
                </select>
              </div>
              <button
                className="btn btn-primary"
                onClick={runWhoIsFree}
                disabled={freeLoading || !wDate}
                style={{ marginBottom: 2 }}
              >
                {freeLoading ? t('availabilityV2.searching') : t('availabilityV2.search')}
              </button>
            </div>

            {freeError && (
              <div className="card" style={{ borderColor: 'var(--danger)', marginTop: 14 }}>
                <strong style={{ color: 'var(--danger)' }}>{t('availabilityV2.failedLoad')}</strong>
                <p style={{ color: 'var(--text-dim)', margin: '6px 0 0', fontSize: '0.88rem' }}>{freeError}</p>
              </div>
            )}

            {!freeError && !freeLoading && freeMembers.length === 0 && (
              <div className="empty-state" style={{ padding: '2rem 1rem' }}>
                <p>{t('availabilityV2.emptyWhoIsFree')}</p>
                <p className="empty-hint">{t('availabilityV2.emptyWhoIsFreeHint')}</p>
              </div>
            )}

            {!freeError && freeMembers.length > 0 && (
              <div className="table-wrapper" style={{ marginTop: 16, overflowX: 'auto' }}>
                <table className="table tbl-dense" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>{t('availabilityV2.name')}</th>
                      <th style={{ textAlign: 'left' }}>{t('availabilityV2.category')}</th>
                      <th style={{ textAlign: 'left' }}>{t('availabilityV2.roles')}</th>
                      <th style={{ textAlign: 'left' }}>{t('availabilityV2.status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freeMembers.map((m) => (
                      <tr key={m.team_member_id}>
                        <td>
                          {m.name}
                          {m.already_on_show && (
                            <span
                              className="badge badge-warning"
                              style={{ marginLeft: 8 }}
                            >
                              {t('availabilityV2.alreadyOnShow')}
                            </span>
                          )}
                        </td>
                        <td style={{ color: 'var(--text-dim)', textTransform: 'capitalize' }}>{m.category}</td>
                        <td style={{ color: 'var(--text-dim)' }}>
                          {m.roles && m.roles.length ? m.roles.join(', ') : '—'}
                        </td>
                        <td>
                          <span className="badge badge-success">
                            {t('availabilityV2.available')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ---------- Load view ---------- */}
          <section>
            <div className="page-header-meta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem' }}>🗓️ {t('availabilityV2.load')}</h2>
              <button className="btn btn-small" onClick={reloadLoad} disabled={loadLoading}>
                {loadLoading ? t('availabilityV2.loading') : t('availabilityV2.refresh')}
              </button>
            </div>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.9rem', margin: '6px 0 16px' }}>
              {t('availabilityV2.loadHint')}
            </p>

            {loadError && (
              <div className="card" style={{ borderColor: 'var(--danger)', marginBottom: 16 }}>
                <strong style={{ color: 'var(--danger)' }}>{t('availabilityV2.failedLoad')}</strong>
                <p style={{ color: 'var(--text-dim)', margin: '6px 0 0', fontSize: '0.88rem' }}>{loadError}</p>
              </div>
            )}

            {!loadError && loadLoading && (
              <p style={{ color: 'var(--text-dim)' }}>{t('availabilityV2.loading')}</p>
            )}

            {!loadError && !loadLoading && loadMembers.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
                <p style={{ color: 'var(--text-dim)' }}>{t('availabilityV2.emptyLoad')}</p>
              </div>
            )}

            {!loadError && !loadLoading && loadMembers.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {loadMembers.map((m) => {
                  const unavailable = m.unavailable || [];
                  const shows = m.shows || [];
                  const onShows = shows.filter((s) => s.on_show);
                  const offShows = shows.filter((s) => !s.on_show);
                  const nothingBlocked = unavailable.length === 0 && shows.length === 0;
                  return (
                    <div key={m.team_member_id} className="card" style={{ padding: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                        <strong style={{ fontSize: '1rem' }}>{m.name}</strong>
                        <span
                          className={m.category === 'musician' ? 'badge badge-success' : 'badge badge-warning'}
                        >
                          {t(m.category === 'musician' ? 'availabilityV2.musician' : 'availabilityV2.crew')}
                        </span>
                        {m.roles && m.roles.length > 0 && (
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                            {m.roles.join(', ')}
                          </span>
                        )}
                      </div>

                      {nothingBlocked && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          <span className="badge badge-success">
                            {t('availabilityV2.free')}
                          </span>
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem', alignSelf: 'center' }}>
                            {t('availabilityV2.nothingBlocked')}
                          </span>
                        </div>
                      )}

                      {unavailable.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                          {unavailable.map((u, i) => (
                            <span
                              key={i}
                              className="badge badge-danger"
                              title={u.reason || undefined}
                            >
                              {t('availabilityV2.unavailableRange', {
                                range:
                                  u.date_from === u.date_to
                                    ? fmtShort(u.date_from)
                                    : `${fmtShort(u.date_from)} – ${fmtShort(u.date_to)}`,
                              })}
                              {u.reason ? ` · ${u.reason}` : ''}
                            </span>
                          ))}
                        </div>
                      )}

                      {onShows.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                          {onShows.map((s) => (
                            <span key={s.show_id} className="badge badge-info">
                              🎤 {t('availabilityV2.onShowLabel', { date: fmtShort(s.date), name: s.name })}
                            </span>
                          ))}
                        </div>
                      )}

                      {offShows.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {offShows.map((s) => (
                            <span key={s.show_id} className="badge badge-info" style={{ opacity: 0.75 }}>
                              {fmtShort(s.date)} · {s.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
